import React, { useState, useEffect, useRef, useCallback } from 'react';
import emailjs from 'emailjs-com';
import styles from './ModalForm.module.scss';
import { ModalOverlay } from '../ModalOverlay/ModalOverlay';
import InputMask from 'react-input-mask';

export const ModalForm = ({ onClose, popupOpen, isPrivacyPolicyOpenOpen }) => {
    const [values, setValues] = useState({ name: '', phone: '', comment: '' });
    const [errors, setErrors] = useState({ name: '', phone: '' });
    const [isChecked, setIsChecked] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const form = useRef();

    // Валидация
    const validateName = (name) => {
        const trimmed = name.trim();
        if (!trimmed) return 'Имя обязательно';
        if (!/^[A-Za-zА-Яа-яЁё\s\-]{2,30}$/.test(trimmed)) {
        return 'Имя должно содержать от 2 до 30 букв, пробелов или дефисов';
        }
        return '';
    };

    const validatePhone = (phone) => {
        if (!phone) return 'Телефон обязателен';
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 11) return 'Введите полный номер';
        return '';
    };

    // Обработчики изменения
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        let error = '';
        if (name === 'name') error = validateName(value);
        if (name === 'phone') error = validatePhone(value);
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        setValues((prev) => ({ ...prev, phone: value }));
        const error = validatePhone(value);
        setErrors((prev) => ({ ...prev, phone: error }));
    };

    const handleCheckboxChange = () => setIsChecked((prev) => !prev);

    // Проверка валидности формы
    const isFormValid = () => {
        const nameValid = values.name.trim().length >= 2 && !errors.name;
        const phoneValid = values.phone.replace(/\D/g, '').length >= 11 && !errors.phone;
        return nameValid && phoneValid && isChecked;
    };

    const sendData = useCallback(
        (evt) => {
        evt.preventDefault();

        const nameError = validateName(values.name);
        const phoneError = validatePhone(values.phone);
        if (nameError || phoneError || !isChecked || isSending) {
            setErrors({ name: nameError, phone: phoneError });
            return;
        }

        setIsSending(true);

        emailjs
            .sendForm(
            'service_55uzw7o',
            'template_61j1xnk',
            form.current,
            'ypJeFxyaiNsyaBjPV'
            )
            .then(
            () => {
                onClose();
                setValues({ name: '', phone: '', comment: '' });
                setErrors({ name: '', phone: '' });
                setIsChecked(false);
                popupOpen();
            },
            (error) => {
                console.error('Ошибка отправки:', error.text);
                alert('Не удалось отправить заявку. Попробуйте позже.');
            }
            )
            .finally(() => {
            setIsSending(false);
            });
        },
        [values, isChecked, isSending, onClose, popupOpen]
    );

    // Закрытие по Escape
    useEffect(() => {
        const handleEscClose = (evt) => {
        if (evt.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscClose);
        return () => document.removeEventListener('keydown', handleEscClose);
    }, [onClose]);

    return (
        <ModalOverlay onClick={onClose}>
        <div className={styles.modal} onClick={(evt) => evt.stopPropagation()}>
            <div className={styles.box}>
            <h3 className={styles.title}>Оставьте Ваш номер телефона</h3>
            <button
                data-test="close-button"
                className={styles.close_icon}
                type="button"
                aria-label="закрыть"
                onClick={onClose}
            />
            </div>
            <p className={styles.text}>
            Мы свяжемся с Вами в самое ближайшее время!
            </p>

            <form className={styles.form} onSubmit={sendData} ref={form}>
            {/* Имя */}
            <div className={styles.box_input}>
                <label className={styles.label}>
                Введите имя
                <input
                    className={`${styles.input} ${errors.name && styles.wrong}`}
                    type="text"
                    name="name"
                    minLength="2"
                    value={values.name}
                    onChange={handleInputChange}
                    pattern="^[A-Za-zА-Яа-яЁё\s\-]{2,30}$"
                    required
                />
                </label>
            </div>
            {errors.name && <span className={styles.input_error}>{errors.name}</span>}

            {/* Телефон */}
            <div className={styles.box_input}>
                <label className={styles.label}>
                Введите телефон
                <InputMask
                    className={`${styles.input} ${errors.phone && styles.wrong}`}
                    type="tel"
                    name="phone"
                    value={values.phone}
                    onChange={handlePhoneChange}
                    mask="+7\(999) 999-99-99"
                    required
                />
                </label>
            </div>
            {errors.phone && <span className={styles.input_error}>{errors.phone}</span>}

            {/* Комментарий */}
            <div className={styles.box_textarea}>
                <label className={styles.label_textarea}>
                Комментарий
                <textarea
                    className={`${styles.textarea} ${styles.scrollbar}`}
                    name="comment"
                    value={values.comment}
                    onChange={handleInputChange}
                />
                </label>
            </div>

            <button
                className={`${styles.button_form} ${
                !isFormValid() && styles.button_disabled
                }`}
                type="submit"
                disabled={!isFormValid() || isSending}
            >
                {isSending ? 'Отправка...' : 'Отправить'}
            </button>

            {/* Чекбокс */}
            <div className={styles.box_item}>
                <div className={styles.icon_boxs}>
                <label
                    className={
                    isChecked ? styles.label_checked : styles.label_checkbox
                    }
                >
                    <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    style={{
                        opacity: 0,
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        margin: 0,
                        cursor: 'pointer',
                    }}
                    />
                    {isChecked && (
                    <svg
                        className={styles.mark}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#FFFCF7"
                        strokeWidth="2"
                    >
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    )}
                </label>
                </div>
                <div className={styles.box_text}>
                <p className={styles.form_text}>
                    Я предоставляю согласие на обработку персональных данных, а
                    также подтверждаю ознакомление и согласие
                </p>
                <p
                    className={styles.button_link}
                    onClick={isPrivacyPolicyOpenOpen}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                    e.key === 'Enter' && isPrivacyPolicyOpenOpen()
                    }
                >
                    с Политикой конфиденциальности
                </p>
                </div>
            </div>
            </form>
        </div>
        </ModalOverlay>
    );
    };